import { useState, useEffect } from 'react';

const API = import.meta.env.VITE_API_URL || '/api';
const STATUS_OPTIONS = ['pending','processing','shipped','delivered','cancelled'];
const statusColor = s => ({ pending:'#FFB800',processing:'#FF6B00',shipped:'#2196F3',delivered:'#4CAF50',cancelled:'#C0392B' }[s]||'#888');

export default function OrdersManager() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [trackingInput, setTrackingInput] = useState('');
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('admin_token');

  const fetchOrders = () => {
    setLoading(true);
    fetch(`${API}/get-orders.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setOrders(d.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchOrders(); }, []);

  const updateStatus = async (id, status) => {
    setSaving(true);
    await fetch(`${API}/update-order-status.php`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ id, status }),
    });
    setSaving(false);
    fetchOrders();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const updateTracking = async () => {
    if (!selected) return;
    setSaving(true);
    await fetch(`${API}/update-tracking.php`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ id: selected.id, tracking_number: trackingInput }),
    });
    setSaving(false);
    fetchOrders();
    setSelected({ ...selected, tracking_number: trackingInput });
  };

  const filtered = orders.filter(o => {
    const matchStatus = filterStatus === 'all' || o.status === filterStatus;
    const matchSearch = !search || String(o.id).includes(search) || (o.customer_name || '').toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const TH = ({ children }) => <th style={{ padding: '.7rem 1rem', textAlign: 'left', fontFamily: "'Cinzel',serif", fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(255,184,0,.5)', fontWeight: 600, whiteSpace: 'nowrap' }}>{children}</th>;

  return (
    <div>
      <h1 style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: '1.5rem', background: 'linear-gradient(135deg,#FFE082,#FF6B00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '1.8rem' }}>📦 Orders Manager</h1>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by Order # or Customer…"
          style={{ flex: 1, minWidth: 200, padding: '.7rem 1rem', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,184,0,.25)', borderRadius: 6, color: '#FFE082', fontFamily: "'Crimson Pro',serif", fontSize: '.9rem', outline: 'none' }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '.7rem 1rem', background: 'rgba(13,5,0,.95)', border: '1px solid rgba(255,184,0,.25)', borderRadius: 6, color: '#FFE082', fontFamily: "'Cinzel',serif", fontSize: '.72rem', letterSpacing: '.1em', outline: 'none', textTransform: 'uppercase' }}>
          <option value="all">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <button onClick={fetchOrders} style={{ padding: '.7rem 1.4rem', background: 'linear-gradient(135deg,#FF6B00,#FFB800)', border: 'none', borderRadius: 6, color: '#0D0500', fontFamily: "'Cinzel',serif", fontSize: '.7rem', letterSpacing: '.12em', textTransform: 'uppercase', cursor: 'pointer', fontWeight: 700 }}>🔄 Refresh</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap: '1.5rem' }}>
        {/* Orders Table */}
        <div style={{ background: 'linear-gradient(135deg,rgba(20,4,0,.95),rgba(26,10,46,.95))', border: '1px solid rgba(255,184,0,.18)', borderRadius: 10, overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'rgba(255,224,130,.4)', fontFamily: "'Cinzel',serif", fontSize: '.8rem' }}>🌀 Loading orders…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'rgba(255,224,130,.4)', fontFamily: "'Cinzel',serif", fontSize: '.8rem' }}>No orders found 🙏</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,184,0,.1)' }}><TH>Order #</TH><TH>Customer</TH><TH>Items</TH><TH>Total</TH><TH>Status</TH><TH>Tracking</TH><TH>Date</TH><TH>Actions</TH></tr></thead>
              <tbody>
                {filtered.map(order => (
                  <tr key={order.id} onClick={() => { setSelected(order); setTrackingInput(order.tracking_number || ''); }}
                    style={{ borderBottom: '1px solid rgba(255,184,0,.06)', cursor: 'pointer', background: selected?.id === order.id ? 'rgba(255,184,0,.06)' : 'transparent', transition: 'background .2s' }}>
                    <td style={{ padding: '.75rem 1rem', fontFamily: "'Cinzel',serif", fontSize: '.72rem', color: '#FFB800' }}>#{order.id}</td>
                    <td style={{ padding: '.75rem 1rem', fontFamily: "'Crimson Pro',serif", fontSize: '.85rem', color: 'rgba(255,224,130,.8)' }}>
                      <div>{order.customer_name || 'Guest'}</div>
                      <div style={{ fontSize: '.72rem', color: 'rgba(255,224,130,.35)' }}>{order.customer_email}</div>
                    </td>
                    <td style={{ padding: '.75rem 1rem', fontFamily: "'Cinzel',serif", fontSize: '.72rem', color: 'rgba(255,224,130,.6)' }}>{order.item_count || '—'}</td>
                    <td style={{ padding: '.75rem 1rem', fontFamily: "'Cinzel',serif", fontSize: '.78rem', color: '#FFB800', fontWeight: 700 }}>₹{Number(order.total_amount || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '.75rem 1rem' }}>
                      <select value={order.status || 'pending'} onClick={e => e.stopPropagation()} onChange={e => updateStatus(order.id, e.target.value)}
                        style={{ background: `${statusColor(order.status)}18`, color: statusColor(order.status), border: `1px solid ${statusColor(order.status)}44`, borderRadius: 20, padding: '.2rem .6rem', fontFamily: "'Cinzel',serif", fontSize: '.6rem', letterSpacing: '.1em', textTransform: 'uppercase', outline: 'none', cursor: 'pointer' }}>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '.75rem 1rem', fontFamily: "'Crimson Pro',serif", fontSize: '.78rem', color: 'rgba(255,224,130,.5)' }}>{order.tracking_number || '—'}</td>
                    <td style={{ padding: '.75rem 1rem', fontFamily: "'Crimson Pro',serif", fontSize: '.78rem', color: 'rgba(255,224,130,.4)' }}>{order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN') : '—'}</td>
                    <td style={{ padding: '.75rem 1rem' }}>
                      <button onClick={e => { e.stopPropagation(); setSelected(order); setTrackingInput(order.tracking_number || ''); }}
                        style={{ background: 'rgba(255,184,0,.12)', border: '1px solid rgba(255,184,0,.25)', borderRadius: 4, padding: '.3rem .7rem', color: '#FFE082', cursor: 'pointer', fontFamily: "'Cinzel',serif", fontSize: '.6rem', letterSpacing: '.1em' }}>
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Order Detail Panel */}
        {selected && (
          <div style={{ background: 'linear-gradient(135deg,rgba(20,4,0,.98),rgba(26,10,46,.98))', border: '1px solid rgba(255,184,0,.25)', borderRadius: 10, padding: '1.5rem', height: 'fit-content', position: 'sticky', top: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: '.78rem', letterSpacing: '.18em', textTransform: 'uppercase', color: '#FFB800' }}>Order #{selected.id}</h3>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: '1px solid rgba(255,184,0,.25)', color: '#FFE082', width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.8rem' }}>✕</button>
            </div>
            <div style={{ marginBottom: '1.2rem', padding: '1rem', background: 'rgba(255,184,0,.05)', borderRadius: 8, border: '1px solid rgba(255,184,0,.1)' }}>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.62rem', letterSpacing: '.15em', textTransform: 'uppercase', color: '#FF6B00', marginBottom: '.5rem' }}>Customer</div>
              <div style={{ fontFamily: "'Crimson Pro',serif", fontSize: '.9rem', color: '#FFE082' }}>{selected.customer_name || 'Guest'}</div>
              <div style={{ fontSize: '.8rem', color: 'rgba(255,224,130,.5)' }}>{selected.customer_email}</div>
              <div style={{ fontSize: '.8rem', color: 'rgba(255,224,130,.5)', marginTop: '.3rem' }}>{selected.customer_phone}</div>
            </div>
            {selected.shipping_address && (
              <div style={{ marginBottom: '1.2rem', padding: '1rem', background: 'rgba(255,184,0,.05)', borderRadius: 8, border: '1px solid rgba(255,184,0,.1)' }}>
                <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.62rem', letterSpacing: '.15em', textTransform: 'uppercase', color: '#FF6B00', marginBottom: '.5rem' }}>Shipping Address</div>
                <div style={{ fontFamily: "'Crimson Pro',serif", fontSize: '.85rem', color: 'rgba(255,224,130,.7)', lineHeight: 1.6 }}>{selected.shipping_address}</div>
              </div>
            )}
            <div style={{ marginBottom: '1.2rem' }}>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.62rem', letterSpacing: '.15em', textTransform: 'uppercase', color: '#FF6B00', marginBottom: '.5rem' }}>Tracking Number</div>
              <div style={{ display: 'flex', gap: '.5rem' }}>
                <input value={trackingInput} onChange={e => setTrackingInput(e.target.value)} placeholder="Enter tracking #"
                  style={{ flex: 1, padding: '.6rem .8rem', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,184,0,.25)', borderRadius: 4, color: '#FFE082', fontFamily: "'Crimson Pro',serif", fontSize: '.9rem', outline: 'none' }} />
                <button onClick={updateTracking} disabled={saving}
                  style={{ padding: '.6rem .9rem', background: 'linear-gradient(135deg,#FF6B00,#FFB800)', border: 'none', borderRadius: 4, color: '#0D0500', cursor: 'pointer', fontFamily: "'Cinzel',serif", fontSize: '.65rem', fontWeight: 700, letterSpacing: '.08em' }}>
                  {saving ? '…' : 'Save'}
                </button>
              </div>
            </div>
            <div style={{ padding: '1rem', background: 'rgba(255,184,0,.05)', borderRadius: 8, border: '1px solid rgba(255,184,0,.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Cinzel',serif", fontSize: '.78rem', color: '#FFE082' }}>
                <span>Total Amount</span>
                <span style={{ color: '#FFB800', fontWeight: 700 }}>₹{Number(selected.total_amount || 0).toLocaleString('en-IN')}</span>
              </div>
              {selected.coupon_code && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Cinzel',serif", fontSize: '.72rem', color: 'rgba(255,224,130,.5)', marginTop: '.4rem' }}>
                  <span>Coupon: {selected.coupon_code}</span>
                  <span style={{ color: '#4CAF50' }}>-₹{Number(selected.discount_amount || 0).toLocaleString('en-IN')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
